import type { Policies } from "../../../api/adminPolicies";

export interface PolicyField {
  key: keyof Policies;
  label: string;
  unit: string;
  description: string;
  /** Lowest value accepted before asking the server; 1 unless 0 has a meaning (e.g. "off"). */
  min?: number;
}

export interface PolicySection {
  id: string;
  title: string;
  description: string;
  fields: PolicyField[];
}

export const SECTIONS: PolicySection[] = [
  {
    id: "passwords",
    title: "Passwords",
    description: "Applies to new passwords and to password changes.",
    fields: [{ key: "passwordMinLength", label: "Minimum length", unit: "characters", description: "The minimum number of characters in a password." }],
  },
  {
    id: "sessions",
    title: "Sessions",
    description: "How long an open session lasts.",
    fields: [
      { key: "sessionIdleMinutes", label: "Maximum inactivity", unit: "minutes", description: "The session ends after this long without activity. We warn 2 minutes before." },
      { key: "sessionMaxHours", label: "Maximum duration", unit: "hours", description: "Hard limit for a session, even if there is activity." },
      {
        key: "rememberMeDays",
        label: "Keep me signed in",
        unit: "days",
        description: "How long people can stay signed in if they tick \"Keep me signed in\". 0 turns the option off. Maximum 30.",
        min: 0,
      },
    ],
  },
  {
    id: "lockout",
    title: "Lockout after failed attempts",
    description: "Protects accounts against attempts to guess passwords.",
    fields: [
      { key: "lockoutMaxFailures", label: "Allowed attempts", unit: "attempts", description: "Failed attempts in a row before the account is locked." },
      { key: "lockoutMinutes", label: "Lockout duration", unit: "minutes", description: "How long the account stays locked." },
    ],
  },
  {
    id: "retention",
    title: "Data retention",
    description: "How long records are kept before they are deleted.",
    fields: [
      { key: "auditRetentionDays", label: "Audit", unit: "days", description: "Audit events (who did what, and when)." },
      { key: "logsRetentionDays", label: "Logs", unit: "days", description: "Technical logs in Loki." },
      { key: "backupRetentionDays", label: "Backups", unit: "days", description: "Database backups." },
    ],
  },
];

/** The lowest value of the fields whose minimum isn't the usual 1. */
export const MIN_BY_KEY: Record<string, number> = Object.fromEntries(
  SECTIONS.flatMap((s) => s.fields).filter((f) => f.min !== undefined).map((f) => [f.key, f.min as number]),
);
